import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Alert, LinearProgress,
  Divider, IconButton, Tooltip, ToggleButton, ToggleButtonGroup,
  Dialog, DialogContent, DialogActions,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SavingsIcon from '@mui/icons-material/Savings';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LayersIcon from '@mui/icons-material/Layers';
import TuneIcon from '@mui/icons-material/Tune';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE || '';

const fmtCurrency = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

const fmtMinutes = (m) => {
  const mins = Math.round(m || 0);
  const h = Math.floor(mins / 60), r = mins % 60;
  return h > 0 ? `${h} hr${h > 1 ? 's' : ''}${r ? ` ${r} min` : ''}` : `${r} min`;
};

// Stable key for a set of module ids - must match estimatePricing.combinationKey on the server
const comboKey = (ids) => [...ids].map(Number).sort((a, b) => a - b).join('+');

// Total dollar amount shaved off a priced stage's line items by any active
// discount (manual, prior-encounter, or combo override) - same discount
// fields already shown in the itemized "Estimate Details" breakdown below,
// surfaced here so it's visible on the stage card itself, not just after
// scrolling to the line-item detail.
const stageDiscountSavings = (items) => (items || []).reduce((sum, i) => {
  if (!i.discountType || i.discountType === 'none' || !(i.discountValue > 0)) return sum;
  const gross = (i.quantity || 1) * (i.unitPrice || 0);
  const net = i.net ?? gross;
  return sum + Math.max(0, gross - net);
}, 0);

// Explains what Curate is and why it's different from a traditional paper
// or spreadsheet surgical quote - opened from the "What is Curate?" link in
// the welcome banner. Content is written for a cosmetic-surgery patient, not
// staff, so it describes capabilities in plain terms rather than naming the
// underlying catalog/pricing-engine features.
function CurateExplainerDialog({ open, onClose }) {
  const point = (Icon, title, body) => (
    <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
      <Icon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
      <Box>
        <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{body}</Typography>
      </Box>
    </Box>
  );
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {/* Placeholder graphic - swap this Box for a <video>/illustration later */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #7b1fa2 0%, #ab47bc 55%, #ec407a 100%)',
          color: 'common.white', px: 3, py: 4, textAlign: 'center', position: 'relative', overflow: 'hidden'
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 40, mb: 1, opacity: 0.95 }} />
        <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.15 }}>
          Curate<sup style={{ fontSize: '0.5em', verticalAlign: 'super' }}>TM</sup>
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.95, mt: 0.5 }}>
          Your personal surgery planner studio
        </Typography>
      </Box>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Surgical quotes have traditionally been written out by hand, one procedure at a time on paper.
          Over the years, many practices moved to spreadsheets, which is faster but still just a single,
          fixed number - if you want to add a procedure, remove one, or ask "what if," someone has to
          redo the math and send you a new page.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Curate is a live pricing system built for patients who want to actually explore their options -
          add procedures, take them away, compare choices, and see real numbers update instantly, without
          waiting on a revised quote.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, p: 1.5, borderRadius: 2, bgcolor: 'secondary.50', border: '1px solid', borderColor: 'secondary.light' }}>
          <VerifiedUserIcon color="secondary" sx={{ mt: 0.25, flexShrink: 0 }} />
          <Typography variant="body2" color="text.secondary">
            Practices that offer Curate are choosing to give you two things most surgical quotes don't:{' '}
            <strong>convenience</strong> - a real transactional platform where you can build, adjust, and act on your
            own plan - and <strong>transparency</strong> - a clear, itemized view of exactly what you're paying for
            and why.
          </Typography>
        </Box>

        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          While you curate your plan, it automatically...
        </Typography>
        {point(SavingsIcon, 'Prices combined surgeries correctly',
          'Procedures done together share one facility fee and one block of anesthesia time instead of two - Curate recognizes this and shows your savings versus having them done separately.')}
        {point(TuneIcon, 'Lets you compare real options',
          'When a procedure has choices - like implant manufacturer, material, or style - Curate shows them side by side with a relative cost indicator ($ / $$ / $$$), so you can see the trade-off before deciding.')}
        {point(LayersIcon, 'Keeps your surgery dates realistic',
          "Every surgeon has a safe maximum length for a single operation. If the procedures you've chosen would run longer than that, Curate automatically suggests splitting your plan into a second surgery date instead of letting it quietly become unrealistic.")}
        {point(LocalOfferIcon, 'Applies discounts the moment you qualify',
          "Bundle pricing, paired (bilateral) procedures, and other qualifying discounts are applied automatically and labeled clearly - you'll always see when, and why, a discount is reflected in your price.")}
        {point(ReceiptLongIcon, 'Shows the real breakdown',
          "Your estimate isn't a single lump sum - you can see it itemized by professional fee, facility fee, anesthesia, and supplies, the same way your care team sees it internally.")}
        {point(FactCheckIcon, 'Still reviewed by your care team',
          "Curate does the math, but a person on your surgeon's team reviews the plan you build before it's finalized - you'll be notified once it's approved, and then you can place your deposit to move forward.")}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Got it</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Patient-facing plan builder for modular estimates.
 *
 * The patient picks procedures (modules) by choice group, optionally across
 * several surgery stages. Which per-stage combinations are allowed, and the
 * single-surgery time cap, come from the server (plan.allowedKeys /
 * plan.overTimeKeys / plan.maxStageMinutes) - the browser never sees per-module
 * prices or the surgeon's rules. Every change re-prices the whole plan via
 * POST /share/:token/plan/price, which returns stage totals, a grand total and
 * the savings versus having each procedure as its own surgery.
 *
 * Props:
 *   token, plan, surgeonName, simplified
 *   initialStages: [[moduleId,...], ...] to reopen a previously chosen plan
 *   initialOptions: { [moduleId]: { [variationGroupId]: optionId } } - patient-chosen variations (e.g. implant type)
 *   onSelected(estimateId): called after POST /plan/select succeeds
 *   EstimateCard: component used to render the priced preview
 */
export default function PlanBuilder({ token, plan, surgeonName, simplified = true, initialStages, initialOptions, onSelected, EstimateCard }) {
  const modules = useMemo(() => plan?.modules || [], [plan]);
  const byId = useMemo(() => Object.fromEntries(modules.map(m => [m.moduleId, m])), [modules]);
  const allowed = useMemo(() => new Set(plan?.allowedKeys || []), [plan]);
  const overTime = useMemo(() => new Set(plan?.overTimeKeys || []), [plan]);
  const cap = plan?.maxStageMinutes || 360;
  // surgeonName arrives as "Last, First" (or "First Last") - address as Dr. <Last>
  const lastName = surgeonName
    ? (surgeonName.includes(',') ? surgeonName.split(',')[0] : surgeonName.split(' ').slice(-1)[0]).trim()
    : null;
  const doctor = lastName ? `Dr. ${lastName}` : 'your surgeon';

  // stages: array of arrays of module ids; activeStage: which stage new picks go into
  const [stages, setStages] = useState(() => (initialStages && initialStages.length ? initialStages.map(s => [...s]) : [[]]));
  const [activeStage, setActiveStage] = useState(0);
  // Patient-chosen variations per module: { [moduleId]: { [groupId]: optionId } }
  const [options, setOptions] = useState(() => initialOptions || {});
  // Supply-family choices (e.g. implant style) depend on the patient's other picks
  // (manufacturer), so each price response refreshes them per module.
  const [liveSupplyChoices, setLiveSupplyChoices] = useState({});
  const choicesFor = (m) => {
    const live = liveSupplyChoices[m.moduleId];
    if (!live) return m.choices || [];
    return [...(m.choices || []).filter(c => c.kind !== 'supply'), ...live];
  };
  const [notice, setNotice] = useState(null);
  const [explainerOpen, setExplainerOpen] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [pricingError, setPricingError] = useState(null);
  const [pricingBusy, setPricingBusy] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [selectError, setSelectError] = useState(null);
  const priceReq = useRef(0);

  const selectedIds = stages.flat();
  const stageOf = (id) => stages.findIndex(s => s.includes(id));
  const stageMinutes = (s) => s.reduce((sum, id) => sum + (byId[id]?.minutes || 0), 0);

  // Group modules into sections by choice group; ungrouped modules each stand alone
  const sections = useMemo(() => {
    const out = [];
    const seen = new Map();
    for (const m of modules) {
      const key = m.choiceGroup || `__single_${m.moduleId}`;
      if (!seen.has(key)) { seen.set(key, out.length); out.push({ key, label: m.choiceGroup || null, modules: [] }); }
      out[seen.get(key)].modules.push(m);
    }
    return out;
  }, [modules]);

  // ─── Re-price whenever the selection changes (debounced) ─────────────────
  useEffect(() => {
    const nonEmpty = stages.filter(s => s.length > 0);
    if (nonEmpty.length === 0) { setPricing(null); setPricingError(null); return; }
    const reqId = ++priceReq.current;
    setPricingBusy(true);
    const t = setTimeout(() => {
      fetch(`${API_BASE}/api/estimates/share/${token}/plan/price`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stages: nonEmpty, options }),
      })
        .then(async (res) => {
          if (reqId !== priceReq.current) return;
          const data = await res.json();
          if (!res.ok) { setPricingError(data.error || 'Unable to price this plan'); setPricing(null); return; }
          setPricingError(null);
          setPricing(data);
          if (data.supplyChoices) setLiveSupplyChoices(prev => ({ ...prev, ...data.supplyChoices }));
        })
        .catch(() => { if (reqId === priceReq.current) setPricingError('Network error - please try again.'); })
        .finally(() => { if (reqId === priceReq.current) setPricingBusy(false); });
    }, 350);
    return () => clearTimeout(t);
  }, [stages, options, token]);

  // ─── Selection logic ────────────────────────────────────────────────────
  const removeModule = (id) => {
    setNotice(null);
    setStages(prev => {
      const next = prev.map(s => s.filter(x => x !== id)).filter(s => s.length > 0);
      return next.length ? next : [[]];
    });
    setActiveStage(0);
  };

  const addModule = (id) => {
    const m = byId[id];
    if (!m) return;
    setNotice(null);
    // Choice group: swap out the sibling currently selected (keep its stage)
    const sibling = m.choiceGroup ? selectedIds.find(x => x !== id && byId[x]?.choiceGroup === m.choiceGroup) : null;
    let working = stages.map(s => s.filter(x => x !== sibling));
    let target = sibling != null ? stageOf(sibling) : activeStage;
    if (target < 0 || target >= working.length) target = Math.min(activeStage, working.length - 1);

    const tryStage = (sIdx) => {
      const key = comboKey([...(working[sIdx] || []), id]);
      return allowed.has(key) ? 'ok' : overTime.has(key) ? 'time' : 'combo';
    };

    let verdict = tryStage(target);
    if (verdict !== 'ok') {
      // Look for another existing stage that can take it
      const alt = working.findIndex((s, i) => i !== target && tryStage(i) === 'ok');
      if (alt >= 0) {
        setNotice({ severity: 'info', text: `${m.label} ${verdict === 'time' ? `would make Stage ${target + 1} longer than ${doctor} performs in one surgery` : `can't be done in the same surgery as the procedures in Stage ${target + 1}`}, so it was added to Stage ${alt + 1}.` });
        target = alt;
      } else if (allowed.has(comboKey([id]))) {
        // Open a new stage for it
        working = [...working, []];
        target = working.length - 1;
        setNotice({ severity: 'info', text: `${m.label} ${verdict === 'time' ? `would exceed the ${fmtMinutes(cap)} ${doctor} performs in one surgery` : `can't be combined with your other selections in one surgery`}, so we added it as Stage ${target + 1} - a separate surgery date.` });
      } else {
        setNotice({ severity: 'warning', text: `${m.label} isn't available with your current selections. Please contact our office if you'd like to discuss it.` });
        return;
      }
    }
    working = working.map((s, i) => i === target ? [...s, id] : s);
    setStages(working);
    setActiveStage(target);
  };

  const moveModule = (id, toStage) => {
    setNotice(null);
    const working = stages.map(s => s.filter(x => x !== id));
    while (working.length <= toStage) working.push([]);
    const key = comboKey([...working[toStage], id]);
    if (!allowed.has(key)) {
      setNotice({ severity: 'warning', text: overTime.has(key)
        ? `Moving ${byId[id]?.label} there would make Stage ${toStage + 1} longer than ${doctor} performs in one surgery.`
        : `${byId[id]?.label} can't be done in the same surgery as the procedures in Stage ${toStage + 1}.` });
      return;
    }
    working[toStage] = [...working[toStage], id];
    setStages(working.filter((s, i) => s.length > 0 || i === 0));
  };

  const addStage = () => { setStages(prev => [...prev, []]); setActiveStage(stages.length); };
  // Remove a stage. Its procedures come out of the plan (the patient can
  // re-add them to another stage); the last remaining stage is just cleared.
  const removeStage = (sIdx) => {
    const removed = stages[sIdx] || [];
    setNotice(removed.length
      ? { severity: 'info', text: `${removed.map(id => byId[id]?.label).join(' + ')} removed from your plan. You can add ${removed.length > 1 ? 'them' : 'it'} back to another stage any time.` }
      : null);
    setStages(prev => {
      const next = prev.filter((_, i) => i !== sIdx);
      return next.length ? next : [[]];
    });
    setActiveStage(0);
  };

  const handleChoose = async () => {
    setSelecting(true); setSelectError(null);
    try {
      const res = await fetch(`${API_BASE}/api/estimates/share/${token}/plan/select`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stages: stages.filter(s => s.length > 0), options }),
      });
      const data = await res.json();
      if (!res.ok) { setSelectError(data.error || 'Unable to save your plan'); return; }
      onSelected?.(data.estimateId);
    } catch {
      setSelectError('Network error - please try again.');
    } finally {
      setSelecting(false);
    }
  };

  const missingRequired = modules.filter(m => m.isRequired && !selectedIds.includes(m.moduleId));
  const hasSelection = selectedIds.length > 0;
  const nonEmptyStages = stages.filter(s => s.length > 0);

  // Preview in the same card used for staff-built estimates
  const previewEstimate = pricing ? {
    variation_label: nonEmptyStages.length > 1 ? 'Your Plan' : (pricing.stages[0]?.labels || []).join(' + '),
    notes: nonEmptyStages.length > 1 ? `${nonEmptyStages.length} surgery stages` : null,
    total_amount: pricing.total,
    stages: pricing.stages.map(s => ({ stageLabel: s.stageLabel, items: s.items })),
  } : null;

  return (
    <Box>
      <Alert
        severity="info"
        icon={<InfoOutlinedIcon />}
        sx={{ mb: 3, alignItems: 'flex-start', '& .MuiAlert-action': { pt: 0.25, alignItems: 'flex-start' } }}
        action={
          <Chip
            component="button"
            type="button"
            onClick={() => setExplainerOpen(true)}
            icon={<AutoAwesomeIcon sx={{ fontSize: '16px !important', color: 'common.white !important' }} />}
            label={<span>What is Curate<sup style={{ fontSize: '0.65em' }}>TM</sup>?</span>}
            clickable
            sx={{
              mr: 1, fontWeight: 700, color: 'common.white', border: 'none', cursor: 'pointer',
              background: 'linear-gradient(135deg, #7b1fa2 0%, #ab47bc 55%, #ec407a 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #6a1b9a 0%, #9c27b0 55%, #d81b60 100%)' },
              '& .MuiChip-label': { px: 1.25 }
            }}
          />
        }
      >
        <strong>Welcome to Curate<sup style={{ fontSize: '0.6em', verticalAlign: 'super' }}>TM</sup>, your personal surgery planner studio.</strong>
        <br />
        Choose the procedures you're interested in and we'll price them together.
        Procedures done in the same surgery share facility and anesthesia time, so combining them usually costs less than
        separate surgeries. {doctor} performs up to <strong>{fmtMinutes(cap)}</strong> in one operation — if your choices run longer,
        you can split them into more than one surgery date.
      </Alert>
      <CurateExplainerDialog open={explainerOpen} onClose={() => setExplainerOpen(false)} />

      {notice && <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice.text}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, gap: 3, alignItems: 'start' }}>
        {/* ── Left: choices ── */}
        <Box>
          {sections.map(sec => (
            <Paper key={sec.key} elevation={1} sx={{ mb: 2, overflow: 'hidden' }}>
              {sec.label && (
                <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary">{sec.label}</Typography>
                  <Typography variant="caption" color="text.secondary">Choose one</Typography>
                </Box>
              )}
              {sec.modules.map(m => {
                const selected = selectedIds.includes(m.moduleId);
                const sIdx = stageOf(m.moduleId);
                return (
                  <Box key={m.moduleId}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'grey.100',
                      bgcolor: selected ? 'primary.50' : 'transparent', cursor: 'pointer', '&:hover': { bgcolor: selected ? 'primary.50' : 'grey.50' } }}
                    onClick={() => selected ? removeModule(m.moduleId) : addModule(m.moduleId)}
                  >
                    {selected
                      ? <CheckCircleIcon color="primary" />
                      : <AddCircleOutlineIcon sx={{ color: 'text.disabled' }} />}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="body1" fontWeight={selected ? 700 : 500}>{m.label}</Typography>
                        <Tooltip title={`About ${fmtMinutes(m.minutes)} of surgery`} arrow>
                          <ScheduleIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
                        </Tooltip>
                        {m.isRequired && <Chip label="Included" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />}
                      </Box>
                      {m.description && <Typography variant="body2" color="text.secondary">{m.description}</Typography>}
                      {/* Patient-factor notes: why this procedure is more involved for this patient (never a choice) */}
                      {selected && (m.notes || []).length > 0 && (
                        <Box sx={{ mt: 1, pl: 1.25, borderLeft: '3px solid', borderColor: 'info.light' }} onClick={e => e.stopPropagation()}>
                          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>About your procedure</Typography>
                          {m.notes.map((n, i) => (
                            <Typography key={i} variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem', lineHeight: 1.4, mb: 0.5 }}>{n.text}</Typography>
                          ))}
                        </Box>
                      )}
                      {selected && choicesFor(m).length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }} onClick={e => e.stopPropagation()}>
                          {choicesFor(m).map(ch => {
                            const current = options[m.moduleId]?.[ch.groupId] ?? ch.selected;
                            return (
                              <Box key={ch.groupId} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ minWidth: 90 }}>{ch.groupName}</Typography>
                                <ToggleButtonGroup size="small" exclusive value={current != null ? Number(current) : null}
                                  onChange={(_, v) => { if (v == null) return; setNotice(null); setOptions(prev => ({ ...prev, [m.moduleId]: { ...(prev[m.moduleId] || {}), [ch.groupId]: v } })); }}>
                                  {ch.options.map(o => (
                                    <ToggleButton key={o.optionId} value={Number(o.optionId)} sx={{ px: 1.25, py: 0.25, fontSize: '0.72rem', textTransform: 'none', gap: 0.6 }}>
                                      {o.label}
                                      {o.priceTier && (
                                        <Tooltip title="Relative cost compared with the other options" arrow>
                                          <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'success.main', letterSpacing: 0.3 }}>{o.priceTier}</Typography>
                                        </Tooltip>
                                      )}
                                    </ToggleButton>
                                  ))}
                                </ToggleButtonGroup>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                    {selected && stages.length > 1 && (
                      <ToggleButtonGroup size="small" exclusive value={sIdx} onClick={e => e.stopPropagation()}
                        onChange={(_, v) => { if (v != null && v !== sIdx) moveModule(m.moduleId, v); }}>
                        {stages.map((_, i) => <ToggleButton key={i} value={i} sx={{ px: 1, py: 0.25, fontSize: '0.7rem' }}>Stage {i + 1}</ToggleButton>)}
                      </ToggleButtonGroup>
                    )}
                    {selected && (
                      <Tooltip title="Remove">
                        <IconButton size="small" onClick={e => { e.stopPropagation(); removeModule(m.moduleId); }}><RemoveCircleOutlineIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
            </Paper>
          ))}
        </Box>

        {/* ── Right: stages + summary ── */}
        <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>Your Plan</Typography>
              {stages.length > 1 && (
                <ToggleButtonGroup size="small" exclusive value={activeStage} onChange={(_, v) => v != null && setActiveStage(v)}>
                  {stages.map((_, i) => <ToggleButton key={i} value={i} sx={{ px: 1, py: 0.25, fontSize: '0.7rem' }}>Stage {i + 1}</ToggleButton>)}
                </ToggleButtonGroup>
              )}
            </Box>
            {stages.length > 1 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                New selections go into <strong>Stage {activeStage + 1}</strong>. Each stage is a separate surgery date.
              </Typography>
            )}

            {stages.map((s, sIdx) => {
              const mins = stageMinutes(s);
              const pct = Math.min(100, Math.round((mins / cap) * 100));
              const priced = pricing?.stages?.find(ps => comboKey(ps.moduleIds) === comboKey(s));
              return (
                <Box key={sIdx} sx={{ mb: 1.5, p: 1.5, border: '1px solid', borderColor: sIdx === activeStage && stages.length > 1 ? 'primary.main' : 'divider', borderRadius: 1 }}>
                  {/* Title: the procedures themselves (prefixed by the stage number when there are several) */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
                      {stages.length > 1 && <Typography component="span" variant="body2" fontWeight={700} color="primary.main">Stage {sIdx + 1}{s.length ? ': ' : ''}</Typography>}
                      {s.length === 0
                        ? <Typography component="span" variant="body2" color="text.disabled" fontWeight={400}>Nothing selected yet</Typography>
                        : s.map(id => byId[id]?.label).join(' + ')}
                    </Typography>
                    {(stages.length > 1 || s.length > 0) && (
                      <Tooltip title={s.length === 0 ? 'Remove this stage' : stages.length > 1 ? 'Remove this stage and its procedures from your plan' : 'Clear this surgery'}>
                        <IconButton size="small" sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }} onClick={() => removeStage(sIdx)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  {/* Time bar: surgery time labelled on the filled portion, the allowable total at the far end */}
                  <Tooltip title={s.length === 0 ? `Up to ${fmtMinutes(cap)} in one operation` : `${fmtMinutes(mins)} of surgery, of the ${fmtMinutes(cap)} ${doctor} performs in one operation`} arrow>
                    <Box sx={{ position: 'relative', height: 22, my: 1, borderRadius: 11, bgcolor: pct >= 100 ? 'warning.light' : 'primary.light', overflow: 'hidden', cursor: 'help' }}>
                      <Box sx={{ position: 'absolute', inset: 0, width: `${pct}%`, bgcolor: pct >= 100 ? 'warning.main' : 'primary.main', borderRadius: 11, transition: 'width 200ms' }} />
                      {s.length > 0 && (
                        // Label lives inside the dark filled portion in white, hugging its right edge;
                        // only when the fill is too short to hold any text does it step just outside.
                        <Typography variant="caption" fontWeight={700}
                          sx={{ position: 'absolute', top: 0, bottom: 0, display: 'flex', alignItems: 'center', px: 1.25, whiteSpace: 'nowrap',
                            ...(pct >= 16
                              ? { left: 0, width: `${pct}%`, justifyContent: 'flex-end', color: 'common.white' }
                              : { left: `${pct}%`, color: 'text.primary' }) }}>
                          {fmtMinutes(mins)}
                        </Typography>
                      )}
                      <Typography variant="caption" fontWeight={600}
                        sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center', px: 1.25, whiteSpace: 'nowrap', color: pct >= 92 ? 'common.white' : 'text.secondary' }}>
                        {fmtMinutes(cap)}
                      </Typography>
                    </Box>
                  </Tooltip>
                  {priced && (() => {
                    const discountSavings = stageDiscountSavings(priced.items);
                    return (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.75 }}>
                        {discountSavings > 0.5 && (
                          <Tooltip title={`Includes ${fmtCurrency(discountSavings)} in discounts`} arrow>
                            <Chip
                              icon={<LocalOfferIcon sx={{ fontSize: '14px !important' }} />}
                              label="Discount applied" size="small" color="error" variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 20, cursor: 'help' }}
                            />
                          </Tooltip>
                        )}
                        <Typography variant="body2" fontWeight={700}>{fmtCurrency(priced.total)}</Typography>
                      </Box>
                    );
                  })()}
                </Box>
              );
            })}

            <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addStage} disabled={stages[stages.length - 1].length === 0}>
              Add another surgery date
            </Button>

            <Divider sx={{ my: 2 }} />

            {pricingBusy && <LinearProgress sx={{ mb: 1 }} />}
            {pricingError && <Alert severity="warning" sx={{ mb: 1 }}>{pricingError}</Alert>}
            {!hasSelection && <Typography variant="body2" color="text.secondary">Select a procedure to see pricing.</Typography>}
            {pricing && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                  <Typography variant="body1" fontWeight={700}>Estimated total</Typography>
                  <Typography variant="h5" fontWeight={800} color="primary.main">{fmtCurrency(pricing.total)}</Typography>
                </Box>
                {pricing.savings > 0.5 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, color: 'success.main' }}>
                    <SavingsIcon fontSize="small" />
                    <Typography variant="body2" fontWeight={600}>
                      You save {fmtCurrency(pricing.savings)} vs. separate surgeries
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {missingRequired.length > 0 && (
              <Alert severity="info" sx={{ mt: 1.5 }}>{missingRequired.map(m => m.label).join(', ')} must be part of your plan.</Alert>
            )}
            {selectError && <Alert severity="error" sx={{ mt: 1.5 }}>{selectError}</Alert>}
            <Button
              variant="contained" size="large" fullWidth sx={{ mt: 2 }}
              disabled={!pricing || pricingBusy || selecting || missingRequired.length > 0}
              onClick={handleChoose}
            >
              {selecting ? 'Saving…' : 'Choose this plan'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Your care team will review the plan you curated, then you'll be able to pay the deposit and continue.
            </Typography>
          </Paper>
        </Box>
      </Box>

      {/* ── Priced preview, same format as a staff-built estimate ── */}
      {previewEstimate && EstimateCard && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>Estimate Details</Typography>
          <EstimateCard estimate={previewEstimate} simplified={simplified} />
        </Box>
      )}
    </Box>
  );
}
